import { markActiveOptions } from './ingredientOptions';

const CATEGORIES = ['protein', 'veggie', 'starch', 'sauce', 'garnish'];

// These ship an `{ id: null, name: 'No Sauce' | 'No Starch' }` option from
// computeDishVariantOptions (server.js), so the dish stays valid without them.
const OPT_OUT_CATEGORIES = ['sauce', 'starch'];

// A dish can't be served without these.
const REQUIRED_CATEGORIES = ['protein'];

function findCategory(ingredientId, optionsByCategory) {
  for (const category of CATEGORIES) {
    if (optionsByCategory[category].some((opt) => opt.id === ingredientId)) {
      return category;
    }
  }
  return null;
}

function hasOptOutOption(options) {
  return (options || []).some((opt) => opt.id == null);
}

/** FNV-1a into mulberry32. Seeded, not random, so the card, the modal it opens
 *  and the page after a reload all show the same replacement. */
function seededRandom(seed) {
  const text = String(seed);
  let h = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Splits a dish's original ingredients into kept/removed based on `conflictsFn`,
 *  then for each removed ingredient picks a non-conflicting replacement from that
 *  ingredient's own category pool (protein/veggie/starch/sauce/garnish), so a
 *  dish never arrives with a category left empty just because of an allergy.
 *
 *  With no non-conflicting option left: sauce/starch take their "No Sauce"/"No
 *  Starch" option, veggie/garnish are dropped, but protein — or an ingredient in
 *  no pool at all, which is the whole dish — can't be designed around. Same for a
 *  conflict in a single-ingredient dish, whatever its category: that ingredient
 *  *is* the dish, so swapping or dropping it leaves nothing to serve. Then
 *  nothing is substituted and `hasAllergenConflict` is set: a half-stripped dish
 *  would still carry the allergen and misrepresent what turns up in the box.
 *
 *  Premium (upcharge) proteins are only eligible for `isPremiumCustomer` — and if
 *  every premium option conflicts for them, falls back to the non-premium pool
 *  rather than leaving protein unselected. Non-protein categories never carry a
 *  `premium` flag, so this gating is a no-op for them. */
export function computeAutoSubstitution({
  originalIngredients = [],
  proteinOptions = [],
  veggieOptions = [],
  starchOptions = [],
  sauceOptions = [],
  garnishOptions = [],
  conflictsFn,
  // Allergy-group names for a conflict ("Tree Nuts"). Per-surface: the meal plan
  // and the storefront hold the customer's restrictions in different shapes.
  conflictLabelsFn,
  isPremiumCustomer = false,
  seed,
  random = Math.random,
}) {
  const optionsByCategory = {
    protein: proteinOptions,
    veggie: veggieOptions,
    starch: starchOptions,
    sauce: sauceOptions,
    garnish: garnishOptions,
  };

  const pick = seed == null ? random : seededRandom(seed);

  const keptIngredients = [];
  const removedIngredients = [];
  (originalIngredients || []).forEach((ingredient) => {
    if (conflictsFn(ingredient)) removedIngredients.push(ingredient);
    else keptIngredients.push(ingredient);
  });

  const keptIds = new Set(keptIngredients.map((i) => i.id));
  const chosenIdsByCategory = { protein: new Set(), veggie: new Set(), starch: new Set(), sauce: new Set(), garnish: new Set() };
  const substitutions = [];
  // The swapped-in ingredients as full option records — they keep their allergen
  // links, so a meal reassigned to someone else can still be judged against what
  // it actually contains now.
  const addedIngredients = [];
  const optedOutCategories = new Set();
  const unresolvable = [];

  // The lone ingredient of a one-ingredient dish is the dish, so a conflict there
  // is unresolvable even in a category that would normally swap or opt out.
  const isSingleIngredientDish = (originalIngredients || []).length === 1;

  removedIngredients.forEach((ingredient) => {
    const category = findCategory(ingredient.id, optionsByCategory);
    if (!category || isSingleIngredientDish) {
      unresolvable.push(ingredient);
      substitutions.push({ category, removedId: ingredient.id, removedName: ingredient.name, addedId: null, addedName: null });
      return;
    }

    const alreadyChosen = chosenIdsByCategory[category];
    const eligible = optionsByCategory[category].filter((opt) =>
      opt.id != null &&
      opt.id !== ingredient.id &&
      !keptIds.has(opt.id) &&
      !alreadyChosen.has(opt.id) &&
      !conflictsFn(opt)
    );

    const premiumEligible = eligible.filter((opt) => opt.premium);
    const nonPremiumEligible = eligible.filter((opt) => !opt.premium);
    const pool = isPremiumCustomer
      ? (premiumEligible.length > 0 ? premiumEligible : nonPremiumEligible)
      : nonPremiumEligible;

    if (pool.length === 0) {
      const canOptOut = OPT_OUT_CATEGORIES.includes(category)
        && hasOptOutOption(optionsByCategory[category]);
      if (canOptOut) optedOutCategories.add(category);
      else if (REQUIRED_CATEGORIES.includes(category)) unresolvable.push(ingredient);
      substitutions.push({
        category,
        removedId: ingredient.id,
        removedName: ingredient.name,
        addedId: null,
        addedName: null,
        optedOut: canOptOut,
      });
      return;
    }

    const picked = pool[Math.floor(pick() * pool.length)];
    alreadyChosen.add(picked.id);
    addedIngredients.push({ id: picked.id, name: picked.name, allergen: picked.allergen || [] });
    substitutions.push({ category, removedId: ingredient.id, removedName: ingredient.name, addedId: picked.id, addedName: picked.name });
  });

  if (unresolvable.length > 0) {
    const originalIds = (originalIngredients || []).map((i) => i.id);
    return {
      keptIngredients: [...originalIngredients],
      removedIngredients: [],
      finalIngredients: [...originalIngredients],
      proteinOptions: markActiveOptions(proteinOptions, originalIds),
      veggieOptions: markActiveOptions(veggieOptions, originalIds),
      starchOptions: markActiveOptions(starchOptions, originalIds),
      sauceOptions: markActiveOptions(sauceOptions, originalIds),
      garnishOptions: markActiveOptions(garnishOptions, originalIds),
      substitutions: [],
      // Every conflict is still in the dish, not just the unreplaceable one.
      allergenConflicts: removedIngredients.map((i) => ({
        id: i.id,
        name: i.name,
        labels: conflictLabelsFn ? conflictLabelsFn(i) : [],
      })),
      hasAllergenConflict: true,
      wasAdjusted: false,
    };
  }

  const addedIds = substitutions.filter((s) => s.addedId).map((s) => s.addedId);
  const selectedIds = [...keptIds, ...addedIds];
  const mark = (category) =>
    markActiveOptions(optionsByCategory[category], selectedIds, optedOutCategories.has(category));

  const finalIngredients = [...keptIngredients, ...addedIngredients];

  return {
    keptIngredients,
    removedIngredients,
    finalIngredients,
    proteinOptions: mark('protein'),
    veggieOptions: mark('veggie'),
    starchOptions: mark('starch'),
    sauceOptions: mark('sauce'),
    garnishOptions: mark('garnish'),
    substitutions,
    allergenConflicts: [],
    hasAllergenConflict: false,
    wasAdjusted: removedIngredients.length > 0,
  };
}
